/**
 * CDP SDK Core Utilities
 * Common utility functions used throughout the SDK
 */

import type { CDPCoreConfig } from './types';

// =============================================================================
// ID GENERATION
// =============================================================================

/**
 * Generate a cryptographically secure random ID (32 hex characters)
 */
export function generateId(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a UUID v4
 */
export function generateUUID(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  // Set version (4) and variant (10xx)
  array[6] = (array[6] & 0x0f) | 0x40;
  array[8] = (array[8] & 0x3f) | 0x80;

  const hex = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// =============================================================================
// LOGGING
// =============================================================================

let debugEnabled = false;

/**
 * Set debug mode
 */
export function setDebugMode(enabled: boolean): void {
  debugEnabled = enabled;
}

/**
 * Log debug messages (only when debug mode is enabled)
 */
export function log(...args: unknown[]): void {
  if (debugEnabled && typeof console !== 'undefined' && console.log) {
    console.log('[CDP]', ...args);
  }
}

/**
 * Log warning messages
 */
export function warn(...args: unknown[]): void {
  if (typeof console !== 'undefined' && console.warn) {
    console.warn('[CDP]', ...args);
  }
}

/**
 * Log error messages
 */
export function error(...args: unknown[]): void {
  if (typeof console !== 'undefined' && console.error) {
    console.error('[CDP]', ...args);
  }
}

// =============================================================================
// TIMING UTILITIES
// =============================================================================

/**
 * Promise-based delay
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Debounce function - delays execution until after wait ms have elapsed
 * since the last invocation
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function debounced(...args: Parameters<T>): void {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func.apply(null, args);
      timeoutId = null;
    }, wait);
  };
}

/**
 * Throttle function - ensures function is called at most once per wait ms
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let lastTime = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function throttled(...args: Parameters<T>): void {
    const now = Date.now();
    const remaining = wait - (now - lastTime);

    if (remaining <= 0) {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastTime = now;
      func.apply(null, args);
    } else if (timeoutId === null) {
      timeoutId = setTimeout(() => {
        lastTime = Date.now();
        timeoutId = null;
        func.apply(null, args);
      }, remaining);
    }
  };
}

// =============================================================================
// OBJECT UTILITIES
// =============================================================================

/**
 * Deep merge two objects
 */
export function deepMerge<T extends object>(
  target: T,
  source: Partial<T>
): T {
  const result = { ...target } as T;

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key as keyof T];
      const targetValue = result[key as keyof T];

      if (
        sourceValue !== null &&
        sourceValue !== undefined &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue !== null &&
        targetValue !== undefined &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        (result as Record<string, unknown>)[key] = deepMerge(
          targetValue as object,
          sourceValue as object
        );
      } else if (sourceValue !== undefined) {
        (result as Record<string, unknown>)[key] = sourceValue;
      }
    }
  }

  return result;
}

/**
 * Remove undefined and null values from object
 */
export function cleanObject<T extends object>(obj: T): Partial<T> {
  const result: Partial<T> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key as keyof T];
      if (value !== undefined && value !== null && value !== '') {
        result[key as keyof T] = value;
      }
    }
  }

  return result;
}

// =============================================================================
// STRING UTILITIES
// =============================================================================

/**
 * Hash a string using djb2 algorithm (fast, non-cryptographic)
 */
export function hashString(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Hash a string using SHA-256 (cryptographic, async)
 */
export async function sha256(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

// =============================================================================
// URL UTILITIES
// =============================================================================

/**
 * Parse URL search params
 */
export function parseSearchParams(search: string): Record<string, string> {
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(search);

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

/**
 * Get UTM parameters from URL
 */
export function getUTMParams(search: string): {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  term?: string | null;
  content?: string | null;
} {
  const params = new URLSearchParams(search);
  return {
    source: params.get('utm_source'),
    medium: params.get('utm_medium'),
    campaign: params.get('utm_campaign'),
    term: params.get('utm_term'),
    content: params.get('utm_content'),
  };
}

// =============================================================================
// BROWSER UTILITIES
// =============================================================================

/**
 * Check if we're in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__cdp_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if cookies are enabled
 */
export function areCookiesEnabled(): boolean {
  try {
    document.cookie = '__cdp_test__=1; SameSite=Lax';
    const enabled = document.cookie.includes('__cdp_test__');
    document.cookie = '__cdp_test__=; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    return enabled;
  } catch {
    return false;
  }
}

// =============================================================================
// RETRY UTILITIES
// =============================================================================

/**
 * Calculate exponential backoff delay with jitter
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelayMs: number = 1000,
  maxDelayMs: number = 30000,
  backoffMultiplier: number = 2
): number {
  const delayMs = baseDelayMs * Math.pow(backoffMultiplier, attempt);
  // Add jitter (±25%)
  const jitter = delayMs * 0.25 * (Math.random() * 2 - 1);
  return Math.min(delayMs + jitter, maxDelayMs);
}

// =============================================================================
// TIMESTAMP UTILITIES
// =============================================================================

/**
 * Get ISO 8601 timestamp
 */
export function getTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Get Unix timestamp in milliseconds
 */
export function getNow(): number {
  return Date.now();
}
